// MIDAS V3 — History page (Phase 5; trimmed MIDAS-V3-BUGS-0519)
//
// Replaces the Phase 1 placeholder body with the full v3 implementation:
//   • Page header: eyebrow "MIDAS / HISTORY" + H1 + mono count badge.
//     (The duplicate `+ Nový chat` button shipped in P5 was removed by
//      MIDAS-V3-BUGS-0519 sub-task 1 — the rail is the sole entry point
//      to the New Chat dialog.)
//   • Search bar — live client-side title filter over loaded threads.
//   • Filter chips row: All / Platform (manager-only) / per-machine chips —
//     server-side via existing `?machine_id=` param.
//   • Sort: client-side reverse toggle (desc ↔ asc) — local state.
//   • Period-grouped row list (Today / Yesterday / This week / Earlier).
//   • Skeleton on initial fetch, empty state when filter zeroes the list.
//
// Data source: existing `midasHistoryPageSlice` (unchanged from Phase 4).
//
// Row click → loadThread(id) + navigate("/midas-v3"). The v3 chat page does
// NOT currently parse a `?thread=` query param; it reads `activeThreadId`
// from the v2 `midasPage` slice (which `loadThread` populates). This matches
// the rail's behaviour after Phase 2. Routing via `?thread=` would require
// changes to MidasChatAreaV3 / MidasPageV3 (out of scope for P5).
//
// Spec: MIDAS_V3_PHASE5_HISTORY_PAGE_SPEC.md
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { Roles } from "../../../../../account/models/AccountModels";
import { getMachines } from "../../../../machinery/customerMachinerySlice";
import { Machine } from "../../../../machinery/models/models";
import { loadThread } from "../../../midasPageSlice";
import {
  fetchHistoryThreads,
  HistoryMachineFilter,
  resetHistoryPage,
  setMachineFilter,
  setSearchQuery,
} from "../../../midasHistoryPageSlice";
import { Thread } from "../../../midasPageModels";
import { bucketOfV3, V3DateBucket } from "../v3-utils/threadBuckets";
import MidasHistoryDateGroupV3 from "./MidasHistoryDateGroupV3";
import MidasHistoryEmptyV3 from "./MidasHistoryEmptyV3";
import MidasHistoryFiltersV3, { HistoryMachineChip } from "./MidasHistoryFiltersV3";
import MidasHistorySearchV3 from "./MidasHistorySearchV3";
import MidasHistorySkeletonV3 from "./MidasHistorySkeletonV3";
import MidasHistorySortV3, { V3HistorySortOrder } from "./MidasHistorySortV3";
import { MidasV3HistoryPageContext } from "./HistoryPageContext";

// ============================================================
// Helpers
// ============================================================

const flattenMachines = (
  machinesMap: Record<string, Machine[] | undefined> | null | undefined
): Machine[] => {
  if (!machinesMap || typeof machinesMap !== "object") return [];
  const flat: Machine[] = [];
  Object.values(machinesMap).forEach((g) => {
    if (Array.isArray(g)) flat.push(...(g as Machine[]));
  });
  return flat;
};

const RENDER_ORDER: V3DateBucket[] = [
  "today",
  "yesterday",
  "thisWeek",
  "lastWeek",
  "earlier",
];

// ============================================================
// Component
// ============================================================

const MidasHistoryPageV3: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("chatBot");

  const t = useCallback(
    (key: string, fallback: string) =>
      translate(key as any, { defaultValue: fallback }) as string,
    [translate]
  );

  // Manager gating — mirrors NewChatDialogV3 (line 178). Worker doesn't see
  // the Platform chip.
  const user = useAppSelector((s) => s.account.user);
  const isManager = !!user?.role && user.role !== Roles.WORKER;

  // ── Slice state ──
  const threads = useAppSelector((s) => s.midasHistoryPage.threads);
  const total = useAppSelector((s) => s.midasHistoryPage.total);
  const loading = useAppSelector((s) => s.midasHistoryPage.loading);
  const error = useAppSelector((s) => s.midasHistoryPage.error);
  const machineFilter = useAppSelector(
    (s) => s.midasHistoryPage.machineFilter
  );
  const searchQuery = useAppSelector((s) => s.midasHistoryPage.searchQuery);
  const seenMachineIds = useAppSelector(
    (s) => s.midasHistoryPage.seenMachineIds
  );
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);
  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const machineryLoading = useAppSelector(
    (s) => s.customerMachinery.loading
  );

  // ── Local UI state ──
  // Sort lives in local state (not slice) — only the history page reads it,
  // server returns DESC and reverse is client-side. Per spec §1: "local is
  // fine since v2 uses updated_at DESC only".
  const [sortOrder, setSortOrder] = useState<V3HistorySortOrder>("desc");

  // ── Search input ref — exposed to MidasV3Layout via context so Ctrl+K
  //    on this page focuses the input directly. ──
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const layoutContext = useContext(MidasV3HistoryPageContext);

  useEffect(() => {
    // Register our input ref with the layout. Layout's keydown handler reads
    // `searchInputRef.current` lazily. We clear on unmount so a Ctrl+K after
    // the page unmounts doesn't try to focus a stale node.
    if (!layoutContext) return;
    layoutContext.searchInputRef.current = searchInputRef.current;
    return () => {
      if (layoutContext.searchInputRef.current === searchInputRef.current) {
        layoutContext.searchInputRef.current = null;
      }
    };
  }, [layoutContext]);

  // ── Initial load + filter-change refetch ──
  // Same pattern as v2 MidasHistoryPageV2 — separate effects for fetch (deps:
  // machineFilter) and unmount cleanup (deps: []), so cleanup doesn't fire
  // on every chip click.
  useEffect(() => {
    dispatch(fetchHistoryThreads({ machineFilter }));
    if (flattenMachines(machinesMap).length === 0 && !machineryLoading) {
      dispatch(getMachines({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineFilter]);

  useEffect(() => {
    return () => {
      dispatch(resetHistoryPage());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Machine name lookup ──
  const machineNameById = useMemo(() => {
    const map: Record<string, string> = {};
    flattenMachines(machinesMap).forEach((m) => {
      const brand = m.customBrandName || m.manufacturer?.name || "";
      const name = brand
        ? `${brand} ${m.model || ""}`.trim()
        : m.model || "";
      if (m.id && name) map[m.id] = name;
    });
    return map;
  }, [machinesMap]);

  // ── Chip list — sticky seen machines + always re-add the active filter ──
  const machineChips: HistoryMachineChip[] = useMemo(() => {
    const ids = new Set<string>(seenMachineIds);
    if (
      machineFilter !== "all" &&
      machineFilter !== "platform" &&
      machineFilter
    ) {
      ids.add(machineFilter);
    }
    return Array.from(ids)
      .map((id) => ({
        id,
        label: machineNameById[id] || id.slice(0, 8),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [seenMachineIds, machineNameById, machineFilter]);

  // ── Client-side search filter ──
  const filteredThreads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => {
      if (t.title.toLowerCase().includes(q)) return true;
      const mLabel = t.machine_id ? machineNameById[t.machine_id] : null;
      if (mLabel && mLabel.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [threads, searchQuery, machineNameById]);

  // ── Date buckets — bucket then sort within each bucket per sortOrder ──
  const grouped = useMemo(() => {
    const buckets: Record<V3DateBucket, Thread[]> = {
      today: [],
      yesterday: [],
      thisWeek: [],
      lastWeek: [],
      earlier: [],
    };
    // Sort all threads once by timestamp DESC (newest first), then assign to
    // buckets. For `asc`, reverse the array before bucketing — bucket order
    // (RENDER_ORDER) still goes today→earlier so the page still reads
    // top-down chronologically.
    const sorted = [...filteredThreads].sort(
      (a, b) =>
        new Date(b.last_message_at || b.created_at).getTime() -
        new Date(a.last_message_at || a.created_at).getTime()
    );
    const ordered = sortOrder === "asc" ? sorted.reverse() : sorted;
    ordered.forEach((t) => {
      const b = bucketOfV3(t.last_message_at || t.created_at);
      buckets[b].push(t);
    });
    return buckets;
  }, [filteredThreads, sortOrder]);

  // ── Handlers ──
  // MIDAS-V3-BUGS-0519 sub-task 1: `handleNewChat` removed — the page no
  // longer renders the `+ Nový chat` button. The rail is the sole entry
  // point to the dialog.

  const handleOpenThread = useCallback(
    (threadId: string) => {
      // v3 chat page doesn't currently parse `?thread=` — it reads
      // `activeThreadId` from the v2 midasPage slice (which loadThread
      // populates). Same pattern v2's history rows use after Phase 4.
      dispatch(loadThread(threadId));
      navigate("/midas-v3");
    },
    [dispatch, navigate]
  );

  const handleMachineFilterChange = useCallback(
    (next: HistoryMachineFilter) => {
      dispatch(setMachineFilter(next));
    },
    [dispatch]
  );

  const handleSearchChange = useCallback(
    (next: string) => {
      dispatch(setSearchQuery(next));
    },
    [dispatch]
  );

  // ── i18n labels (resolved once per render so child components stay pure) ──
  const eyebrow = t("midas.v3.history.pageEyebrow", "MIDAS / HISTORY");
  const pageTitle = t("midas.v3.history.pageTitle", "All your chat history");
  // MIDAS-V3-BUGS-0519 sub-task 1: newChatLabel no longer needed — button removed.
  const searchPlaceholder = t(
    "midas.v3.history.searchPlaceholder",
    "Search threads, machines, SR numbers, message content…"
  );
  const searchAria = t(
    "midas.v3.history.searchAriaLabel",
    "Search conversations"
  );
  const filterEyebrow = t("midas.v3.history.filterEyebrow", "FILTER");
  const filterAll = t("midas.v3.history.filter.all", "All chats");
  const filterPlatform = t("midas.v3.history.filter.platform", "Platform");
  const sortNewest = t("midas.v3.history.sort.newest", "Newest");
  const sortOldest = t("midas.v3.history.sort.oldest", "Oldest");
  const groupToday = t("midas.v3.history.group.today", "Today");
  const groupYesterday = t("midas.v3.history.group.yesterday", "Yesterday");
  const groupThisWeek = t("midas.v3.history.group.thisWeek", "This week");
  const groupLastWeek = t("midas.v3.history.group.lastWeek", "Last week");
  const groupEarlier = t("midas.v3.history.group.earlier", "Earlier");
  const countSuffix = t("midas.v3.history.group.countSuffix", "chats");
  const msgsLabel = t("midas.v3.history.msgs", "msgs");
  const emptyTitle = t("midas.v3.history.empty.title", "No matches");
  const emptyHint = t(
    "midas.v3.history.empty.hint",
    "Try a different filter or clear the search."
  );
  const platformTagLabel = t("midas.v3.history.platformTagLabel", "PLATFORM");

  const bucketLabel = (b: V3DateBucket): string => {
    switch (b) {
      case "today":
        return groupToday;
      case "yesterday":
        return groupYesterday;
      case "thisWeek":
        return groupThisWeek;
      case "lastWeek":
        return groupLastWeek;
      case "earlier":
        return groupEarlier;
    }
  };

  // ── Body ──
  let body: React.ReactNode;
  if (loading && threads.length === 0) {
    body = <MidasHistorySkeletonV3 />;
  } else if (error && threads.length === 0) {
    body = (
      <MidasHistoryEmptyV3
        title={t(
          "midas.v3.history.error.title",
          "Couldn't load your history"
        )}
        body={t(
          "midas.v3.history.error.body",
          "Something went wrong. Try again in a moment."
        )}
      />
    );
  } else if (filteredThreads.length === 0) {
    body = <MidasHistoryEmptyV3 title={emptyTitle} body={emptyHint} />;
  } else {
    body = (
      <div>
        {RENDER_ORDER.map((b) => (
          <MidasHistoryDateGroupV3
            key={b}
            bucket={b}
            threads={grouped[b]}
            label={bucketLabel(b)}
            countSuffix={countSuffix}
            dateHint=""
            machineNameById={machineNameById}
            activeThreadId={activeThreadId}
            onOpenThread={handleOpenThread}
            platformLabel={platformTagLabel}
            msgsLabel={msgsLabel}
          />
        ))}
      </div>
    );
  }

  return (
    <main
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        background: "var(--midas-v3-bg)",
      }}
    >
      {/* Page header (fixed at top of content column) */}
      <div
        style={{
          padding: "20px 32px 0",
          background: "var(--midas-v3-bg)",
          flexShrink: 0,
        }}
      >
        <div style={{ maxWidth: 920, margin: "0 auto" }}>
          {/* MIDAS-V3-BUGS-0519 sub-task 1: the right-side `+ Nový chat`
              button was removed. The header is now title-only — the rail's
              "+ New chat" pill remains the sole entry to the dialog. */}
          <div style={{ minWidth: 0 }}>
            <div
              className="midas-v3-eyebrow"
              style={{ marginBottom: 4 }}
            >
              {eyebrow}
            </div>
            <h1
              style={{
                margin: 0,
                fontWeight: 700,
                fontSize: 30,
                letterSpacing: "-0.02em",
                color: "var(--midas-v3-ink)",
                display: "flex",
                alignItems: "baseline",
                gap: 12,
                flexWrap: "wrap",
                lineHeight: 1.15,
              }}
            >
              {pageTitle}
              <span
                className="mono"
                aria-label={`${total} ${countSuffix}`}
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "var(--midas-v3-faint)",
                }}
              >
                {total}
              </span>
            </h1>
          </div>

          {/* Search bar */}
          <MidasHistorySearchV3
            ref={searchInputRef}
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            ariaLabel={searchAria}
          />

          {/* Filter chips + sort */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 14,
              marginBottom: 20,
              flexWrap: "wrap",
            }}
          >
            <MidasHistoryFiltersV3
              active={machineFilter}
              machines={machineChips}
              showPlatformChip={isManager}
              onChange={handleMachineFilterChange}
              filterEyebrow={filterEyebrow}
              allLabel={filterAll}
              platformLabel={filterPlatform}
            />
            <span style={{ flex: 1 }} />
            <MidasHistorySortV3
              value={sortOrder}
              onChange={setSortOrder}
              newestLabel={sortNewest}
              oldestLabel={sortOldest}
            />
          </div>
        </div>
      </div>

      {/* Scrollable list */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: "0 32px 28px",
          // When a filter re-fetch is in flight AND we still have stale rows
          // (the slice no longer wipes threads on filter change — see
          // midasHistoryPageSlice.ts setMachineFilter comment), dim the list
          // to signal "refreshing". Single opacity transition replaces the
          // empty→skeleton→rows triple-flash.
          opacity: loading && threads.length > 0 ? 0.55 : 1,
          transition: "opacity .15s ease",
        }}
        aria-busy={loading && threads.length > 0}
      >
        <div style={{ maxWidth: 920, margin: "0 auto" }}>{body}</div>
      </div>
    </main>
  );
};

export default MidasHistoryPageV3;
